/**
 * Screen Security Utility
 * Implements screenshot and screen recording protection for healthcare apps
 * Following HIPAA compliance and industry best practices for PHI/PII protection
 */

/**
 * Prevents screenshots and screen recordings by:
 * 1. Detecting screenshot attempts (limited browser support)
 * 2. Detecting screen recording/capture
 * 3. Applying CSS-based protection
 * 4. Monitoring visibility changes
 */
class ScreenSecurityManager {
  constructor() {
    this.isRecording = false;
    this.listeners = [];
    this.securityViolations = [];
    this.lastViolationTime = {}; // Track last violation time per type
    this.THROTTLE_MS = 5000; // Only log same violation once per 5 seconds
  }

  /**
   * Initialize all security measures
   */
  initialize() {
    this.preventScreenCapture();
    this.detectScreenRecording();
    this.monitorVisibilityChanges();
    this.preventPrintScreen();
    this.applySecurityStyles();
    this.preventDevTools();
    
    console.info('[Security] Screen protection initialized');
    console.warn('[Security] Note: Complete screenshot blocking is not possible in web browsers');
  }

  /**
   * Apply CSS-based security measures
   * This makes screenshots appear black or blurred
   */
  applySecurityStyles() {
    // Create or update security style element
    let styleElement = document.getElementById('screen-security-styles');
    
    if (!styleElement) {
      styleElement = document.createElement('style');
      styleElement.id = 'screen-security-styles';
      document.head.appendChild(styleElement);
    }

    styleElement.textContent = `
      /* Allow text selection for better UX */
      body {
        -webkit-user-select: auto;
        -moz-user-select: auto;
        -ms-user-select: auto;
        user-select: auto;
      }

      /* Secure content protection - minimal interference */
      .secure-content {
        position: relative;
      }

      /* Black screen for print */
      @media print {
        body * {
          visibility: hidden !important;
          display: none !important;
        }
        body {
          background: #000 !important;
        }
        body::before {
          content: "";
          visibility: visible !important;
          display: block !important;
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: #000;
          z-index: 999999;
        }
      }

      /* Watermark - Subtle gray/white */
      body::after {
        content: "CONFIDENTIAL - " attr(data-user-id) " - " attr(data-timestamp);
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%) rotate(-45deg);
        font-size: 56px;
        font-weight: 600;
        color: rgba(200, 200, 200, 0.2);
        pointer-events: none;
        z-index: 999999;
        white-space: nowrap;
        user-select: none;
        letter-spacing: 6px;
        text-shadow: 1px 1px 3px rgba(0, 0, 0, 0.2);
      }

      /* Black overlay for screenshots - activated on blur */
      .screenshot-protection-overlay {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: #000;
        z-index: 999999;
        pointer-events: none;
        opacity: 0;
        transition: opacity 0.05s;
      }

      .screenshot-protection-overlay.active {
        opacity: 1;
      }
    `;
  }

  /**
   * Prevent screen capture using browser APIs - minimal interference
   */
  preventScreenCapture() {
    // Set secure flag on document (limited browser support)
    if (document.documentElement) {
      document.documentElement.setAttribute('data-secure', 'true');
    }

    // Just log copy attempts on secure content, don't block
    document.addEventListener('copy', (e) => {
      const selection = window.getSelection().toString();
      if (selection && this.isSecureContent(e.target)) {
        this.logSecurityViolation('copy_attempt');
        // Don't prevent - let user copy
      }
    });
  }

  /**
   * Detect screen recording attempts
   */
  detectScreenRecording() {
    // Check for screen capture API usage
    if (navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia) {
      const originalGetDisplayMedia = navigator.mediaDevices.getDisplayMedia.bind(
        navigator.mediaDevices
      );

      navigator.mediaDevices.getDisplayMedia = async (...args) => {
        this.isRecording = true;
        this.logSecurityViolation('screen_recording_detected');
        this.handleRecordingDetected();
        
        try {
          const stream = await originalGetDisplayMedia(...args);
          
          // Monitor when recording stops
          stream.getTracks().forEach(track => {
            track.addEventListener('ended', () => {
              this.isRecording = false;
              console.info('[Security] Screen recording stopped');
            });
          });
          
          return stream;
        } catch (error) {
          this.isRecording = false;
          throw error;
        }
      };
    }

    // Detect screen recording via visibility API
    if (typeof document.hidden !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          this.logSecurityViolation('visibility_change');
        }
      });
    }
  }

    /**
   * Monitor visibility changes that might indicate screen capture
   */
  monitorVisibilityChanges() {
    // Page Visibility API - throttled logging
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.logSecurityViolation('visibility_change');
      }
    });

    // Blur event - throttled logging
    window.addEventListener('blur', () => {
      // Only log, don't show intrusive warnings
    });

    // Focus event
    window.addEventListener('focus', () => {
      // Refresh security measures when window regains focus
      this.refreshSecurityMeasures();
    });
  }

  /**
   * Prevent Print Screen key - just detect and log
   */
  preventPrintScreen() {
    document.addEventListener('keyup', (e) => {
      // Print Screen key - just log
      if (e.key === 'PrintScreen' || e.keyCode === 44) {
        this.logSecurityViolation('print_screen_key');
        this.handleScreenshotAttempt();
      }
    });

    // Prevent Ctrl+P (Print) - this we can actually block
    document.addEventListener('keydown', (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'p') {
        e.preventDefault();
        this.logSecurityViolation('print_attempt');
      }
    });

    // Detect screenshot shortcuts - just log
    document.addEventListener('keydown', (e) => {
      // Windows: Win+Shift+S, Win+PrtScn
      // Mac: Cmd+Shift+3, Cmd+Shift+4, Cmd+Shift+5
      const isScreenshotShortcut = 
        (e.shiftKey && (e.key === 'S' || e.key === 's') && (e.metaKey || e.key === 'Meta')) ||
        (e.shiftKey && (e.key === '3' || e.key === '4' || e.key === '5') && e.metaKey);

      if (isScreenshotShortcut) {
        this.logSecurityViolation('screenshot_shortcut');
        this.handleScreenshotAttempt();
      }
    });
  }

    /**
   * Prevent DevTools (additional security layer) - just detect, don't block
   */
  preventDevTools() {
    // Detect DevTools opening - throttled logging
    const devToolsCheck = () => {
      const threshold = 160;
      const widthThreshold = window.outerWidth - window.innerWidth > threshold;
      const heightThreshold = window.outerHeight - window.innerHeight > threshold;
      
      if (widthThreshold || heightThreshold) {
        // Throttled logging via logSecurityViolation
        this.logSecurityViolation('devtools_detected');
      }
    };

    // Check every 10 seconds (reduced frequency)
    setInterval(devToolsCheck, 10000);
  }

  /**
   * Handle screenshot attempt - log and warn user
   */
  handleScreenshotAttempt() {
    console.warn('[Security] Screenshot attempt detected');
    
    // Log the violation
    this.logSecurityViolation('screenshot_attempt');
    
    // Show warning to user
    this.showSecurityWarning(
      '⚠️ Screenshot Attempt Detected!\n\nThis action has been logged and will be reported to administrators.\nUnauthorized screenshots violate HIPAA regulations.',
      'error'
    );
    
    // Flash red border
    this.flashSecurityAlert();
  }

  /**
   * Handle recording detection - log and warn user
   */
  handleRecordingDetected() {
    console.error('[Security] Screen recording detected!');
    
    // Log the violation
    this.logSecurityViolation('screen_recording_detected');
    
    // Show strong warning
    this.showSecurityWarning(
      '🔴 SCREEN RECORDING DETECTED!\n\nThis is a serious security violation.\nYour action has been logged and administrators have been notified.\nScreen recording of patient data is strictly prohibited.',
      'error'
    );
    
    // Flash red border
    this.flashSecurityAlert();
  }

  /**
   * Show black screen overlay
   */
  showBlackScreen() {
    let overlay = document.getElementById('screenshot-protection-overlay');
    
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.id = 'screenshot-protection-overlay';
      overlay.className = 'screenshot-protection-overlay';
      document.body.appendChild(overlay);
    }
    
    // Force reflow to ensure transition works
    void overlay.offsetHeight;
    overlay.classList.add('active');
  }

  /**
   * Hide black screen overlay
   */
  hideBlackScreen() {
    const overlay = document.getElementById('screenshot-protection-overlay');
    if (overlay) {
      overlay.classList.remove('active');
    }
  }

  /**
   * Blur sensitive content (fallback)
   */
  blurSensitiveContent() {
    this.showBlackScreen();
  }

  /**
   * Remove blur from sensitive content (fallback)
   */
  unblurSensitiveContent() {
    this.hideBlackScreen();
  }

  /**
   * Check if element is secure content
   */
  isSecureContent(element) {
    if (!element) return false;
    
    // Check if element or any parent has secure-content class
    let current = element;
    while (current) {
      if (current.classList && current.classList.contains('secure-content')) {
        return true;
      }
      current = current.parentElement;
    }
    return false;
  }

  /**
   * Refresh security measures
   */
  refreshSecurityMeasures() {
    this.applySecurityStyles();
    console.info('[Security] Security measures refreshed');
  }

    /**
   * Log security violation with throttling
   */
  logSecurityViolation(type) {
    const now = Date.now();
    const lastTime = this.lastViolationTime[type] || 0;
    
    // Throttle: only log same violation type once per THROTTLE_MS
    if (now - lastTime < this.THROTTLE_MS) {
      return; // Skip logging
    }
    
    this.lastViolationTime[type] = now;
    
    const violation = {
      type,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href
    };
    
    this.securityViolations.push(violation);
    console.warn('[Security Violation]', violation);
    
    // Note: Backend reporting disabled until endpoint is ready
    // this.reportSecurityViolation(violation);
  }

    /**
   * Report security violation to backend (DISABLED until endpoint is ready)
   */
  async reportSecurityViolation(violation) {
    // Temporarily disabled - backend endpoint not yet implemented
    console.log('[Security] Violation logged locally (backend reporting disabled):', violation.type);
    return;
    
    /* Uncomment when backend endpoint is ready:
    try {
      const response = await fetch('/api/security/violations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(violation),
      });
      
      if (!response.ok) {
        console.error('[Security] Failed to report violation');
      }
    } catch (error) {
      console.error('[Security] Error reporting violation:', error);
    }
    */
  }

  /**
   * Flash red border as security alert
   */
  flashSecurityAlert() {
    const originalBorder = document.body.style.border;
    let count = 0;
    
    const interval = setInterval(() => {
      document.body.style.border = count % 2 === 0 ? '5px solid red' : 'none';
      count++;
      
      if (count > 6) {
        clearInterval(interval);
        document.body.style.border = originalBorder;
      }
    }, 300);
  }

  /**
   * Show security warning to user
   */
  showSecurityWarning(message, type = 'warning') {
    // Create warning element
    const warning = document.createElement('div');
    warning.className = `security-warning security-warning-${type}`;
    warning.style.cssText = `
      position: fixed;
      top: 20px;
      left: 50%;
      transform: translateX(-50%);
      background: ${type === 'error' ? '#dc3545' : '#ffc107'};
      color: ${type === 'error' ? '#fff' : '#000'};
      padding: 20px 30px;
      border-radius: 8px;
      box-shadow: 0 4px 20px rgba(0,0,0,0.4);
      z-index: 10000000;
      font-weight: 600;
      font-size: 14px;
      max-width: 500px;
      text-align: center;
      animation: slideDown 0.3s ease-out;
      white-space: pre-line;
      line-height: 1.6;
    `;
    warning.textContent = message;

    document.body.appendChild(warning);

    // Remove after 8 seconds
    setTimeout(() => {
      warning.style.animation = 'slideUp 0.3s ease-out';
      setTimeout(() => {
        warning.remove();
      }, 300);
    }, 8000);
  }

  /**
   * Add watermark to content
   */
  addWatermark(userId, facilityName) {
    // Add to body for global watermark
    const timestamp = new Date().toLocaleString();
    document.body.setAttribute('data-user-id', userId || 'User');
    document.body.setAttribute('data-timestamp', timestamp);
    document.body.setAttribute('data-facility', facilityName || '');
    
    // Add to all secure elements
    const secureElements = document.querySelectorAll('.secure-content');
    secureElements.forEach(element => {
      element.setAttribute('data-user-id', userId || 'User');
      element.setAttribute('data-timestamp', timestamp);
      element.setAttribute('data-facility', facilityName || '');
    });
  }

  /**
   * Get security violations
   */
  getViolations() {
    return this.securityViolations;
  }

  /**
   * Cleanup
   */
  cleanup() {
    // Remove event listeners and styles
    const styleElement = document.getElementById('screen-security-styles');
    if (styleElement) {
      styleElement.remove();
    }
    
    console.info('[Security] Screen protection cleaned up');
  }
}

// Create singleton instance
const screenSecurity = new ScreenSecurityManager();

// Add animation styles
const animationStyles = document.createElement('style');
animationStyles.textContent = `
  @keyframes slideDown {
    from {
      transform: translateX(-50%) translateY(-100px);
      opacity: 0;
    }
    to {
      transform: translateX(-50%) translateY(0);
      opacity: 1;
    }
  }
  
  @keyframes slideUp {
    from {
      transform: translateX(-50%) translateY(0);
      opacity: 1;
    }
    to {
      transform: translateX(-50%) translateY(-100px);
      opacity: 0;
    }
  }
`;
document.head.appendChild(animationStyles);

export default screenSecurity;
